import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleNotch, ArrowSquareOut, Warning, CheckCircle, CaretDown } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

import type { ManifestSection, SettingsManifest } from '@/lib/settingsApi';
import { usePreferences, useUpdatePreferences } from '@/lib/preferences';
import { useFormat, usePracticeTimezone } from '@/lib/format';
import {
  fetchCalendarSyncStatus, enableCalendarSync, disableCalendarSync,
  type CalendarSyncProviderId,
} from '@/lib/calendarSyncApi';
import { beginOAuthConnect } from '@/lib/mailboxApi';
import { getApiErrorMessage } from '@/lib/api';
import { FieldRow, Panel, SaveIndicator, SectionIntro, TextInput, Toggle, useSaveState, useSettingLookup } from './primitives';

/**
 * Diary & Calendar.
 *
 * The personal half of the diary. Working days, working hours and the view this person's diary
 * opens on are THEIRS — they change how the diary looks and behaves for one member, and they do
 * not touch the chambers diary policy, which is an organisation setting with its own section and
 * its own authority.
 *
 * That boundary is the reason these are separate: a barrister who works Saturdays can say so
 * without asserting that chambers operates on Saturdays, and a clerk marking themselves part-time
 * does not shorten anyone else's week.
 */

const DAYS = [
  { iso: 1, label: 'Mon' }, { iso: 2, label: 'Tue' }, { iso: 3, label: 'Wed' },
  { iso: 4, label: 'Thu' }, { iso: 5, label: 'Fri' }, { iso: 6, label: 'Sat' }, { iso: 7, label: 'Sun' },
] as const;

const VIEWS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'agenda', label: 'Agenda' },
] as const;

const RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'fortnight', label: 'Fortnight' },
  { value: 'month', label: 'This month' },
] as const;

const PROVIDER_LABEL: Record<CalendarSyncProviderId, string> = {
  google_calendar: 'Google Calendar',
  microsoft_365: 'Microsoft 365',
};

function Pills<T extends string>({
  value, options, onChange, busy,
}: {
  value: T | null;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (v: T) => void;
  busy?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={busy}
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition disabled:opacity-50
            ${value === o.value
              ? 'border-brand-500 bg-brand-500/10 text-brand-700 dark:text-brand-300'
              : 'border-border text-foreground hover:bg-muted'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Sync on or off, per connected provider. Never creates a connection — that is done elsewhere. */
function CalendarConnectionPanel() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({ queryKey: ['calendar-sync-status'], queryFn: fetchCalendarSyncStatus });
  const { state, error, run } = useSaveState();
  const { formatDateTime } = useFormat();
  const [busy, setBusy] = useState<CalendarSyncProviderId | null>(null);
  const [reconnectError, setReconnectError] = useState<string | null>(null);

  if (isLoading) {
    return <div className="flex h-16 items-center justify-center"><CircleNotch size={16} className="animate-spin text-muted-foreground/50" /></div>;
  }

  // A failed load says nothing about what is connected — never "No calendar is connected yet".
  if (isError) {
    return <div className="text-sm text-muted-foreground">Couldn't load your calendar connections. Refresh the page to try again.</div>;
  }

  // Include connections whose sign-in has expired: they need reconnecting, not a fresh "Connect".
  const connected = (data?.providers ?? []).filter((p) => p.connected || p.needsReconnect);

  if (connected.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No calendar is connected yet.{' '}
        {/* Was /barrister/settings/apps — the "coming soon" integrations placeholder, a dead end. */}
        <Link to="/barrister/settings/calendar" className="inline-flex items-center gap-1 font-medium text-brand-600 hover:underline dark:text-brand-400">
          Connect one <ArrowSquareOut className="h-3 w-3" />
        </Link>
      </div>
    );
  }

  const reconnect = async (provider: CalendarSyncProviderId, mailboxId: string | null) => {
    if (!mailboxId) return;
    setBusy(provider);
    setReconnectError(null);
    try {
      window.location.href = await beginOAuthConnect(
        provider === 'google_calendar' ? 'google' : 'microsoft',
        { mailboxId, returnTo: 'calendar' },
      );
    } catch (err) {
      setBusy(null);
      setReconnectError(getApiErrorMessage(err, 'Could not start the reconnection'));
    }
  };

  return (
    <div className="space-y-2">
      {reconnectError && <p role="alert" className="text-xs text-red-600 dark:text-red-400">{reconnectError}</p>}
      {connected.map((p) => p.needsReconnect ? (
        <div key={p.provider} className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 px-4 py-3 dark:border-amber-500/30">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              {PROVIDER_LABEL[p.provider]}
              <Warning size={13} weight="fill" className="text-amber-500" />
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {p.emailAddress ?? 'Connected account'} · Reconnect needed — the sign-in expired or was revoked
            </div>
          </div>
          <button
            type="button"
            disabled={busy === p.provider}
            onClick={() => void reconnect(p.provider, p.mailboxId)}
            className="shrink-0 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
          >
            {busy === p.provider ? 'Redirecting…' : 'Reconnect'}
          </button>
        </div>
      ) : (
        <div key={p.provider} className="flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              {PROVIDER_LABEL[p.provider]}
              {p.lastSyncError
                ? <Warning size={13} weight="fill" className="text-amber-500" />
                : <CheckCircle size={13} weight="fill" className="text-emerald-500" />}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {p.emailAddress ?? 'Connected'}
              {' · '}
              {p.lastSyncError
                ? p.lastSyncError
                : p.lastSyncAt
                  ? `last synced ${formatDateTime(p.lastSyncAt)}`
                  : 'not synced yet'}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {busy === p.provider && <CircleNotch size={13} className="animate-spin text-muted-foreground" />}
            <Toggle
              label={`Sync ${PROVIDER_LABEL[p.provider]}`}
              checked={p.syncEnabled}
              onChange={(next) => {
                setBusy(p.provider);
                void run(async () => {
                  await (next ? enableCalendarSync(p.provider) : disableCalendarSync(p.provider));
                  await qc.invalidateQueries({ queryKey: ['calendar-sync-status'] });
                }).finally(() => setBusy(null));
              }}
            />
          </div>
        </div>
      ))}
      <SaveIndicator state={state === 'saved' ? 'idle' : state} error={error} />
    </div>
  );
}

export function DiarySection({ section }: { section: ManifestSection; manifest: SettingsManifest }) {
  const setting = useSettingLookup(section);
  const prefs = usePreferences();
  const save = useUpdatePreferences();
  const practiceTimezone = usePracticeTimezone();
  const { state, error, run } = useSaveState();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  /**
   * Same arrow as Security, Notifications and Appearance. "How your diary opens" is left open —
   * it is the first thing on the page and the one most likely to be the reason for the visit.
   */
  const [openHours, setOpenHours] = useState(false);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [openSharing, setOpenSharing] = useState(false);

  const caret = (open: boolean, toggle: () => void, what: string) => (
    <button
      type="button"
      onClick={toggle}
      aria-expanded={open}
      aria-label={`${open ? 'Hide' : 'Show'} ${what}`}
      className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted/50 hover:text-foreground"
    >
      <CaretDown size={12} weight="bold" className={`transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
  );

  const change = (key: string, patch: Parameters<typeof save>[0]) => {
    setBusyKey(key);
    void run(() => save(patch)).finally(() => setBusyKey(null));
  };

  const toggleDay = (iso: number) => {
    const next = prefs.workingDays.includes(iso)
      ? prefs.workingDays.filter((d) => d !== iso)
      : [...prefs.workingDays, iso].sort((a, b) => a - b);
    change('workingDays', { workingDays: next });
  };

  return (
    <div className="space-y-5">
      <SectionIntro title="Diary & Calendar" description="How your diary opens, when you work, and what syncs" />

      {setting('diary.viewPreferences') && (
      <Panel title="How your diary opens" description="Applies to your diary only">
        <FieldRow
          setting={setting('diary.viewPreferences')}
          label="Default view"
          readOnlyValue={prefs.diaryDefaultView ?? 'Month'}
        >
          <Pills
            value={prefs.diaryDefaultView as (typeof VIEWS)[number]['value'] | null}
            busy={busyKey === 'view'}
            onChange={(v) => change('view', { diaryDefaultView: v })}
            options={VIEWS}
          />
        </FieldRow>

        <FieldRow
          setting={setting('diary.viewPreferences')}
          label="Default range"
          readOnlyValue={prefs.diaryDefaultRange ?? 'Month'}
        >
          <Pills
            value={prefs.diaryDefaultRange as (typeof RANGES)[number]['value'] | null}
            busy={busyKey === 'range'}
            onChange={(v) => change('range', { diaryDefaultRange: v })}
            options={RANGES}
          />
        </FieldRow>
      </Panel>
      )}

      <Panel
        title="When you work"
        description="Shades your diary and describes your own availability"
        action={caret(openHours, () => setOpenHours((v) => !v), 'working days and hours')}
      >
        {openHours && <>
        <FieldRow setting={setting('diary.workingHours')} label="Working days" readOnlyValue={prefs.workingDays.join(', ')}>
          <div>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d) => {
                const on = prefs.workingDays.includes(d.iso);
                return (
                  <button
                    key={d.iso}
                    type="button"
                    aria-pressed={on}
                    disabled={busyKey === 'workingDays'}
                    onClick={() => toggleDay(d.iso)}
                    className={`w-12 rounded-lg border px-2 py-1.5 text-xs font-medium transition disabled:opacity-50
                      ${on ? 'border-brand-500 bg-brand-500/10 text-brand-700 dark:text-brand-300' : 'border-border text-muted-foreground hover:bg-muted'}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
            {/* The scope boundary, stated where the choice is made. */}
            <p className="mt-2 text-xs text-muted-foreground">
              Yours alone. This does not change your chambers’ working week or anyone else’s diary.
            </p>
          </div>
        </FieldRow>

        <FieldRow
          setting={setting('diary.workingHours')}
          label="Working hours"
          hint={`Wall clock in ${prefs.effective.timezone}`}
          readOnlyValue={
            prefs.workingHoursStart && prefs.workingHoursEnd
              ? `${prefs.workingHoursStart}–${prefs.workingHoursEnd}`
              : 'Not set'
          }
        >
          {/*
            Both halves are `flex-1` with `min-w-0`, so they are measured identically and share
            whatever the row has — equal to the pixel, and no dead space at the right end. This
            is only safe because TextInput now carries `appearance-none`; without it Safari keeps
            the time control's intrinsic width and the field paints outside its own box.
          */}
          <div className="flex w-full items-center gap-2.5">
            <div className="min-w-0 flex-1">
              <TextInput
                type="time"
                value={prefs.workingHoursStart ?? ''}
                onChange={(v) => change('hoursStart', { workingHoursStart: v || null })}
              />
            </div>
            <span className="shrink-0 text-sm text-muted-foreground">to</span>
            <div className="min-w-0 flex-1">
              <TextInput
                type="time"
                value={prefs.workingHoursEnd ?? ''}
                onChange={(v) => change('hoursEnd', { workingHoursEnd: v || null })}
              />
            </div>
          </div>
        </FieldRow>
        </>}
      </Panel>

      {setting('diary.calendarConnection') && (
        <Panel
          title="Connected calendar"
          description={`Two-way sync. Events keep the practice’s time (${practiceTimezone}) whoever is looking.`}
          action={caret(openCalendar, () => setOpenCalendar((v) => !v), 'connected calendar')}
        >
          {openCalendar && <CalendarConnectionPanel />}
        </Panel>
      )}

      {(setting('diary.icalSharing') || setting('diary.availability')) && (
        <Panel
          title="Sharing and availability"
          description="Capabilities with a page of their own"
          action={caret(openSharing, () => setOpenSharing((v) => !v), 'sharing and availability')}
        >
          {openSharing && <div className="space-y-2">
            {setting('diary.icalSharing') && (
              <Link
                to="/barrister/settings/calendar"
                className="flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-3 text-sm transition hover:bg-muted"
              >
                <span>
                  <span className="font-medium text-foreground">Diary sharing link</span>
                  <span className="block text-xs text-muted-foreground">A read-only feed others can subscribe to</span>
                </span>
                <ArrowSquareOut className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </Link>
            )}
            {setting('diary.availability') && (
              <Link
                to="/barrister/availability"
                className="flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-3 text-sm transition hover:bg-muted"
              >
                <span>
                  <span className="font-medium text-foreground">Availability</span>
                  <span className="block text-xs text-muted-foreground">Periods you are unavailable, for your clerks to work around</span>
                </span>
                <ArrowSquareOut className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </Link>
            )}
          </div>}
        </Panel>
      )}

      {/*
        Said rather than hidden. Three diary preferences are described in the model and have no
        storage yet — choosing which calendars sync, your own reminder timings, and booking
        defaults. Offering a control for them would be a control over nothing, so they are named
        here instead. They are not part of this phase.
      */}
      <p className="text-xs text-muted-foreground">
        Not available yet: choosing which of your calendars sync, your own reminder timings, and
        booking defaults.
      </p>

      <SaveIndicator state={state} error={error} />
    </div>
  );
}
